import { cn } from '@utazzo/ui';
import React from 'react';

import { useMarketingDailyCaps } from '../use-marketing-reports';

import { GRAND_TOTAL_ROW, GRID_TABLE, REPORT_HEAD_CELL } from './month-table';

import type { MarketingDailyCapCell } from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

const PINNED = 'bg-background sticky left-0 z-10 border-r-2 border-border';

/** One cell: the seller's marketing leads/day for that destination; blank when not set. */
const CapCell = ({ cell }: { cell: MarketingDailyCapCell | undefined }): React.JSX.Element => {
  if (!cell || cell.cap === null) return <span className="text-muted-foreground/40">-</span>;
  return <span className="tabular-nums">{cell.cap}</span>;
};

/** Sum of what is set; cells without a value simply do not count. */
const sumCaps = (cells: ReadonlyArray<MarketingDailyCapCell | undefined>): number => {
  let total = 0;
  for (const c of cells) if (c && c.cap !== null) total += c.cap;
  return total;
};

const isSet = (cell: MarketingDailyCapCell | undefined): boolean =>
  Boolean(cell && cell.cap !== null);

/**
 * Marketing leads/day per seller and destination: sellers down the side,
 * destinations across the top, from the Marketing / day box on the user form.
 */
export const DailyCapTable = (): React.JSX.Element => {
  const caps = useMarketingDailyCaps();
  const data = caps.data;
  // Only what is set: a seller with no marketing / day anywhere and a
  // destination nobody has set it for are left out of the sheet.
  const agents = (data?.agents ?? []).filter((a) => Object.values(a.cells).some(isSet));
  const destinations = (data?.destinations ?? []).filter((d) =>
    agents.some((a) => isSet(a.cells[d.id])),
  );
  const intl = destinations.filter((d) => d.isInternational).length;

  return (
    <ReportSection title="Daily lead requirement">
      {caps.isError && (
        <p className="text-destructive p-3 text-sm">Could not load the marketing / day sheet.</p>
      )}
      {!caps.isError && agents.length === 0 && (
        <p className="text-muted-foreground p-3 text-sm">
          {caps.isLoading ? 'Loading...' : 'No seller has a marketing / day set yet.'}
        </p>
      )}
      {agents.length > 0 && (
        <table className={cn(GRID_TABLE, 'text-sm')}>
          <thead>
            <tr>
              <th rowSpan={2} className={cn(REPORT_HEAD_CELL, PINNED, 'text-left align-middle')}>
                Seller
              </th>
              {intl > 0 && (
                <th colSpan={intl} className={cn(REPORT_HEAD_CELL, 'text-center')}>
                  International
                </th>
              )}
              {destinations.length - intl > 0 && (
                <th
                  colSpan={destinations.length - intl}
                  className={cn(REPORT_HEAD_CELL, 'text-center')}
                >
                  Domestic
                </th>
              )}
              <th rowSpan={2} className={cn(REPORT_HEAD_CELL, 'text-center align-middle')}>
                Total
              </th>
            </tr>
            <tr>
              {destinations.map((d) => (
                <th key={d.id} className={cn(REPORT_HEAD_CELL, 'text-center whitespace-nowrap')}>
                  {d.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id}>
                <td className={cn(PINNED, 'px-3 py-2 font-medium whitespace-nowrap')}>
                  {a.name}
                  {!a.autoAssign && (
                    <span
                      className="text-muted-foreground ml-1.5 text-[11px]"
                      title="Switched out of auto-assignment on the user form"
                    >
                      (manual only)
                    </span>
                  )}
                </td>
                {destinations.map((d) => (
                  <td key={d.id} className="px-3 py-2 text-center">
                    <CapCell cell={a.cells[d.id]} />
                  </td>
                ))}
                <td className="px-3 py-2 text-center font-semibold tabular-nums">
                  {sumCaps(destinations.map((d) => a.cells[d.id]))}
                </td>
              </tr>
            ))}
            {/* Per-destination capacity: how many leads a day the floor can
                take for it once everyone mapped is in. */}
            <tr className={GRAND_TOTAL_ROW}>
              <td className={cn(PINNED, 'bg-muted px-3 py-2')}>Total</td>
              {destinations.map((d) => (
                <td key={d.id} className="px-3 py-2 text-center tabular-nums">
                  {sumCaps(agents.map((a) => a.cells[d.id]))}
                </td>
              ))}
              <td className="px-3 py-2 text-center tabular-nums">
                {sumCaps(agents.flatMap((a) => destinations.map((d) => a.cells[d.id])))}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </ReportSection>
  );
};
